import React, { useContext, useState, useMemo } from 'react'
import Axios from 'axios'
import i18next from 'i18next'
import moment from 'moment'
import { useHistory } from 'react-router-dom'
import { Button, CircularProgress } from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { Context } from '../../../Store'
import { stepOneSchema, stepTwoSchema, stepThreeSchema, stepFourSchema } from './zodValidations'
import { BOOKING_STATUS, CHECKOUT_LAST_STEP, ISO_3166_ITALY_COUNTRY_CODE, PAYMENT_TYPE } from './../../../constants'
import { CardNumberElement, useElements, useStripe } from '@stripe/react-stripe-js'
import toast from 'react-hot-toast'
import { isMobile } from 'react-device-detect'

export const NextButton = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [state, dispatch] = useContext(Context)
  const language = i18next.language || 'en'
  const showFlexibilityOptions = useMemo(() => !!state.booking.has_zappy_flex_plan, [state.booking.has_zappy_flex_plan])

  const history = useHistory()
  const { t } = useTranslation()
  const stripe = useStripe()
  const elements = useElements()

  const statePayload = {
    start_date: moment(state.start_date).format('YYYY-MM-DD'),
    end_date:
      state.end_date === null
        ? moment(state.start_date).add(4, 'years').subtract(1, 'day').format('YYYY-MM-DD')
        : moment(state.end_date).format('YYYY-MM-DD'),
    no_guest: state.how_many_people,
    tenant_age: state.age,
    tenant_gender: state.gender,
    tenant_nationality: state.nationality,
    tenant_comment: state.about_yourself,
    tenant_reason: state.profession,
    tenant_proposal: state.tenant_proposal ? parseInt(state.tenant_proposal) : null,
    payment_type: state.payment_type,
    flexibility_status: state.flexibility_status,
  }

  const validateStepSubmission = async () => {
    const isValid = await isStepValid()
    if (isValid === false) {
      return
    }

    const container = document.getElementById('form-container')

    if (isMobile && container) {
      container.scrollTo({ top: 0, behavior: 'smooth' })
    }

    if (state.current_step === CHECKOUT_LAST_STEP) {
      setIsLoading(true)
      dispatch({ type: 'SET_ERROR', payload: null })

      let cardObject = {}
      if (state.payment_type === PAYMENT_TYPE.CARD && state.booking.status === BOOKING_STATUS.DRAFT) {
        const { error, token } = await stripe.createToken(elements.getElement(CardNumberElement), {
          currency: state.property.currency,
        })

        if (error) {
          setIsLoading(false)
          console.error(error)
          return
        }

        cardObject = {
          card_token: token.id,
          card_holder_name: state?.card_holder_name,
          card_holder_email: state?.card_holder_email,
          card_holder_address: state?.card_holder_address,
          card_holder_city: state?.card_holder_city,
          card_holder_zip: state?.card_holder_zip,
          card_holder_country: state?.card_holder_country,
        }
      }

      const updatedBookingInfo = {
        ...state.booking,
        ...statePayload,
        ...cardObject,
      }

      const bookingResponse = await isBookingCheckoutSubmitted(updatedBookingInfo)

      if (!bookingResponse) {
        toast.error('Oops, an error occurred when finishing your booking. Please try again later.')
        setIsLoading(false)
        return
      }

      toast.success("We're completing your booking, please wait a moment...")

      setTimeout(() => {
        window.location.href = `${process.env.REACT_APP_WEBSITE}/${language}/thankyou?booking_id=${state.booking.id}`
      }, 2000)

      return
    }

    if (!showFlexibilityOptions && state.current_step === 2) {
      dispatch({ type: 'SET_CURRENT_STEP', payload: 4 })
    } else if (state.nationality && state.nationality !== ISO_3166_ITALY_COUNTRY_CODE && state.current_step === 2) {
      dispatch({ type: 'SET_CURRENT_STEP', payload: 4 })
    } else {
      dispatch({ type: 'SET_CURRENT_STEP', payload: state.current_step + 1 })
    }
  }

  const isStepValid = async () => {
    dispatch({ type: 'SET_ERROR', payload: null })

    switch (state.current_step) {
      case 1:
        return await isStepOneValid()
      case 2:
        return await isStepTwoValid()
      case 3:
        return await isStepThreeValid()
      case 4:
        return await isStepFourValid()
      default:
        return true
    }
  }

  const isStepOneValid = async () => {
    const result = stepOneSchema.safeParse({
      ...state,
    })

    if (result.success === false) {
      const errors = result.error.flatten().fieldErrors
      dispatch({ type: 'SET_ERROR', payload: errors })
      toastStepError(result)
      return false
    }

    await updateBookingWhenStepIsValid()
    await setStepHistory()
    return true
  }

  const isStepTwoValid = async () => {
    const result = stepTwoSchema.safeParse({
      ...state,
    })

    if (result.success === false) {
      const errors = result.error.flatten().fieldErrors
      dispatch({ type: 'SET_ERROR', payload: errors })
      toastStepError(result)
      return false
    }

    await updateBookingWhenStepIsValid()
    await setStepHistory()
    return true
  }

  const isStepThreeValid = async () => {
    const result = stepThreeSchema.safeParse({
      ...state,
    })

    if (result.success === false) {
      const errors = result.error.flatten().fieldErrors
      dispatch({ type: 'SET_ERROR', payload: errors })
      toastStepError(result)
      return false
    }

    await updateBookingWhenStepIsValid()
    await setStepHistory()
    return true
  }

  const isStepFourValid = async () => {
    const minPrice = (parseInt(state?.property?.price) / 100) * 75

    const result = stepFourSchema(minPrice).safeParse({
      ...state,
      tenant_proposal: state.tenant_proposal ? parseInt(state.tenant_proposal) : null,
    })

    if (result.success === false) {
      const errors = result.error.flatten().fieldErrors
      dispatch({ type: 'SET_ERROR', payload: errors })
      toastStepError(result)
      return false
    }

    await updateBookingWhenStepIsValid()
    await setStepHistory()
    return true
  }

  const isNextDisabled = () => {
    if (isLoading) {
      return true
    }

    if (state.current_step !== 4 || state.booking.status === BOOKING_STATUS.PENDING) {
      return false
    }

    const termsAndConditionNotAccepted = state.terms_and_conditions === false
    const paymentTypeNotSelected = !state.payment_type
    const bankTransferSelectedWithNoReceipt =
      state.payment_type === PAYMENT_TYPE.BANK_TRANSFER && state.is_receipt_file_uploaded === false

    return termsAndConditionNotAccepted || paymentTypeNotSelected || bankTransferSelectedWithNoReceipt
  }

  const setStepHistory = async () => {
    history.replace(`/bookings/${state.booking.id}/${state.current_step}`)
  }

  const isBookingCheckoutSubmitted = async (payload) => {
    try {
      if (state.booking.status === BOOKING_STATUS.PENDING) {
        await Axios.put(process.env.REACT_APP_BOOKING_API + '/update-pending', { ...payload })
        return true
      }

      await Axios.post(process.env.REACT_APP_CONFIRM_API, { ...payload })
      return true
    } catch (error) {
      console.error(error)
      return false
    }
  }

  const updateBookingWhenStepIsValid = async () => {
    try {
      const updatedPayload = {
        ...state.booking,
        ...statePayload,
      }

      await Axios.post(`${process.env.REACT_APP_BOOKING_API}`, updatedPayload)
        .then((response) => response.data)
        .catch((error) => {
          console.error(error)
        })
    } catch (e) {
      console.error(error)
    }
  }
  const toastStepError = (result) => {
    const fieldName = result.error.issues[0].path
      .toString()
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase())
    const errorMessage = result.error.issues[0].message
    toast.error(`An error on ${fieldName} field: \n ${errorMessage}`)
  }

  return (
    <Button
      color="primary"
      variant="contained"
      size="large"
      type="button"
      onClick={validateStepSubmission}
      disabled={isNextDisabled()}
    >
      {isLoading && <CircularProgress size={20} color="white" style={{ marginRight: '10px' }} />}
      {t('form.next')}
    </Button>
  )
}
